import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from '../../../app/LanguageContext';
import { attributeOptionsForDisplay } from './lineItemCatalogConversion';
import {
  createEmptyDraftAttribute,
  mergeCatalogWithDraftKeys,
} from './lineItemCatalogAttributeUtils';

export default function LineItemCatalogAttributesSection({
  catalogAttributes,
  draftAttributes,
  onDraftAttributesChange,
  attributeValues,
  onAttributeValuesChange,
  pendingNewOptions,
  onPendingNewOptionsChange,
  extraOptionsByAttrKey,
  onExtraOptionsByAttrKeyChange,
  disabled = false,
}) {
  const { translate } = useTranslation();

  const catalogVariationAttrs = (catalogAttributes || []).filter(
    (a) => a.is_variation && !a._isDraft,
  );

  const catalogRequiredAttrs = (catalogAttributes || []).filter(
    (a) => a.is_required && !a.is_customizable && !a.is_variation && !a._isDraft,
  );

  const mergedForSelect = mergeCatalogWithDraftKeys(catalogAttributes, draftAttributes);
  const selectableAttrs = mergedForSelect.filter((a) => a.is_variation);
  const draftOnlyVariationSelect = selectableAttrs.length > 0 && selectableAttrs.every((a) => a._isDraft);

  const addDraft = () => {
    onDraftAttributesChange([
      ...draftAttributes,
      createEmptyDraftAttribute(draftAttributes.length),
    ]);
  };

  const updateDraft = (localId, patch) => {
    onDraftAttributesChange(
      draftAttributes.map((d) => (d.localId === localId ? { ...d, ...patch } : d)),
    );
  };

  const removeDraft = (localId) => {
    onDraftAttributesChange(draftAttributes.filter((d) => d.localId !== localId));
  };

  const commitDraftOption = (localId) => {
    const draft = draftAttributes.find((d) => d.localId === localId);
    if (!draft) return;
    const trimmed = String(draft.optionDraft ?? '').trim();
    if (!trimmed) return;
    const opts = draft.options || [];
    if (opts.includes(trimmed)) {
      updateDraft(localId, { optionDraft: '' });
      return;
    }
    updateDraft(localId, { options: [...opts, trimmed], optionDraft: '' });
  };

  const commitCatalogOption = (attr) => {
    const attrKey = attr.key || attr.attribute_key;
    const trimmed = String(pendingNewOptions?.[attrKey] ?? '').trim();
    if (!trimmed || !attrKey) return;
    const knownOpts = attributeOptionsForDisplay(attr, extraOptionsByAttrKey);
    if (knownOpts.includes(trimmed)) {
      onPendingNewOptionsChange({ ...pendingNewOptions, [attrKey]: '' });
      if (!String(attributeValues?.[attrKey] ?? '').trim()) {
        onAttributeValuesChange({ ...attributeValues, [attrKey]: trimmed });
      }
      return;
    }
    const prev = extraOptionsByAttrKey?.[attrKey] || [];
    if (prev.includes(trimmed)) {
      onPendingNewOptionsChange({ ...pendingNewOptions, [attrKey]: '' });
      return;
    }
    onExtraOptionsByAttrKeyChange({
      ...extraOptionsByAttrKey,
      [attrKey]: [...prev, trimmed],
    });
    onPendingNewOptionsChange({ ...pendingNewOptions, [attrKey]: '' });
    if (!String(attributeValues?.[attrKey] ?? '').trim()) {
      onAttributeValuesChange({ ...attributeValues, [attrKey]: trimmed });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle2" color="text.secondary">
        {translate('orders.register_line_item_attributes_section')}
      </Typography>

      {catalogRequiredAttrs.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="caption" color="text.disabled">
            {translate('orders.register_line_item_required_product_attrs')}
          </Typography>
          {catalogRequiredAttrs.map((attr) => {
            const attrKey = attr.key || attr.attribute_key;
            const opts = attributeOptionsForDisplay(attr, extraOptionsByAttrKey);
            return (
              <FormControl key={attr.id || attrKey} fullWidth size="small" disabled={disabled}>
                <InputLabel id={`register-line-req-${attrKey}`}>{attr.name || attrKey}</InputLabel>
                <Select
                  labelId={`register-line-req-${attrKey}`}
                  label={attr.name || attrKey}
                  value={attributeValues[attrKey] || ''}
                  onChange={(e) =>
                    onAttributeValuesChange({ ...attributeValues, [attrKey]: e.target.value })
                  }
                >
                  {opts.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          })}
        </Box>
      ) : null}

      {catalogVariationAttrs.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="caption" color="text.disabled">
            {translate('orders.register_line_item_existing_variation_attrs')}
          </Typography>
          {catalogVariationAttrs.map((attr) => {
            const attrKey = attr.key || attr.attribute_key;
            const opts = attributeOptionsForDisplay(attr, extraOptionsByAttrKey);
            const pendingVal = pendingNewOptions?.[attrKey] ?? '';
            return (
              <Box
                key={attr.id || attrKey}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {attr.name || attrKey}
                </Typography>
                {opts.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {opts.map((opt) => (
                      <Chip key={opt} label={opt} size="small" variant="outlined" />
                    ))}
                  </Stack>
                ) : null}
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={translate('orders.register_line_item_option_add')}
                    value={pendingVal}
                    disabled={disabled}
                    onChange={(e) =>
                      onPendingNewOptionsChange({ ...pendingNewOptions, [attrKey]: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitCatalogOption(attr);
                      }
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={disabled || !String(pendingVal).trim()}
                    onClick={() => commitCatalogOption(attr)}
                  >
                    {translate('orders.register_line_item_add_option')}
                  </Button>
                </Stack>
                <FormControl fullWidth size="small" disabled={disabled}>
                  <InputLabel id={`register-line-val-${attrKey}`}>
                    {translate('orders.register_line_item_sku_value')}
                  </InputLabel>
                  <Select
                    labelId={`register-line-val-${attrKey}`}
                    label={translate('orders.register_line_item_sku_value')}
                    value={attributeValues[attrKey] || ''}
                    onChange={(e) =>
                      onAttributeValuesChange({ ...attributeValues, [attrKey]: e.target.value })
                    }
                  >
                    {opts.map((opt) => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            );
          })}
        </Box>
      ) : null}

      {selectableAttrs.length > 0 && catalogVariationAttrs.length === 0 && !draftOnlyVariationSelect ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="caption" color="text.disabled">
            {translate('orders.register_line_item_attribute_values')}
          </Typography>
          {selectableAttrs.map((attr) => {
            const attrKey = attr.key || attr.attribute_key;
            const opts = attributeOptionsForDisplay(attr, extraOptionsByAttrKey);
            const pendingVal = pendingNewOptions?.[attrKey] ?? '';
            return (
              <FormControl key={attr.id || attrKey} fullWidth size="small" disabled={disabled}>
                <InputLabel id={`register-line-attr-${attrKey}`}>{attr.name || attrKey}</InputLabel>
                <Select
                  labelId={`register-line-attr-${attrKey}`}
                  label={attr.name || attrKey}
                  value={attributeValues[attrKey] || ''}
                  onChange={(e) =>
                    onAttributeValuesChange({ ...attributeValues, [attrKey]: e.target.value })
                  }
                >
                  {opts.map((opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ))}
                </Select>
                <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={translate('orders.register_line_item_option_add')}
                    value={pendingVal}
                    disabled={disabled}
                    onChange={(e) =>
                      onPendingNewOptionsChange({ ...pendingNewOptions, [attrKey]: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (attr._isDraft) {
                          const t = String(pendingVal).trim();
                          if (!t) return;
                          onAttributeValuesChange({ ...attributeValues, [attrKey]: t });
                          onPendingNewOptionsChange({ ...pendingNewOptions, [attrKey]: '' });
                        } else {
                          commitCatalogOption(attr);
                        }
                      }
                    }}
                  />
                  {!attr._isDraft ? (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={disabled || !String(pendingVal).trim()}
                      onClick={() => commitCatalogOption(attr)}
                    >
                      {translate('orders.register_line_item_add_option')}
                    </Button>
                  ) : null}
                </Stack>
              </FormControl>
            );
          })}
        </Box>
      ) : null}

      <Divider />

      <Typography variant="caption" color="text.disabled">
        {translate('orders.register_line_item_new_variation_hint')}
      </Typography>

      {draftAttributes.map((draft) => (
        <Box
          key={draft.localId}
          sx={{
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              fullWidth
              label={translate('orders.register_line_item_attr_name')}
              value={draft.name}
              disabled={disabled}
              onChange={(e) => updateDraft(draft.localId, { name: e.target.value })}
            />
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => removeDraft(draft.localId)}
              aria-label={translate('common.delete')}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {(draft.options || []).map((opt) => (
              <Chip
                key={opt}
                label={opt}
                size="small"
                onDelete={
                  disabled
                    ? undefined
                    : () =>
                        updateDraft(draft.localId, {
                          options: (draft.options || []).filter((o) => o !== opt),
                        })
                }
              />
            ))}
          </Stack>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              fullWidth
              placeholder={translate('orders.register_line_item_option_add')}
              value={draft.optionDraft || ''}
              disabled={disabled}
              onChange={(e) => updateDraft(draft.localId, { optionDraft: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitDraftOption(draft.localId);
                }
              }}
            />
            <Button size="small" disabled={disabled} onClick={() => commitDraftOption(draft.localId)}>
              {translate('common.add')}
            </Button>
          </Stack>
        </Box>
      ))}

      <Button
        size="small"
        startIcon={<AddIcon />}
        disabled={disabled}
        onClick={addDraft}
        sx={{ alignSelf: 'flex-start' }}
      >
        {translate('orders.register_line_item_add_variation_attr')}
      </Button>
    </Box>
  );
}
